/**
 * DCF Skill Content — discounted cash flow valuation guidance.
 *
 * This is the domain knowledge the planner, executor, and verifier receive
 * when the planner fetches the "dcf" skill via `get_skill`. It covers:
 * unlevered FCF build, WACC, terminal value, discounting, EV-to-equity
 * bridge, sensitivity, depth calibration, update/edit traps, and
 * DCF-specific validation rules.
 *
 * Condensed from the Notion DCF Skill document, reconciled with existing
 * prompt guardrails (cell comments unreliable, formatting rules separate).
 */

export const DCF_SKILL_CONTENT = `## Skill: DCF - Discounted Cash Flow

### Purpose
Value a business as the present value of unlevered cash flows over an explicit forecast horizon, plus a terminal value for the period beyond, then bridge enterprise value to equity value per share.

### What Good Looks Like
A good DCF answers five questions at a glance:
1. What is it worth, and how does that compare to where it trades?
2. What cash flows are being valued? (unlevered, after-tax, over an explicit horizon)
3. What discount rate and terminal assumptions drive it?
4. How does enterprise value bridge to equity value per share?
5. How sensitive is the answer to the assumptions that matter most?

Deliver these sections in this order:
- Assumptions block: horizon, WACC inputs, terminal growth and/or exit multiple, tax rate, mid-year toggle, net-debt date.
- Unlevered FCF build: EBITDA -> EBIT -> NOPAT -> +D&A -> -capex -> -change in NWC, per forecast year.
- Discounting: explicit period discount factors at WACC; mid-year convention applied via a toggle.
- Terminal value: computed two ways (Gordon growth and exit multiple), reconciled.
- EV-to-equity bridge: EV - net debt - preferred - minority + associates = equity value; / fully diluted shares = value per share.
- Sensitivity: 2-way tables on WACC x {terminal growth or exit multiple}.
- Output summary: implied EV, equity value and value per share, shown against current share price with upside/downside %.
- Key assumptions visible on the same screen as the output.
- Source line: data providers and "as of" date.

Every input carries visible provenance; every calculation is a formula linked to other cells, never a typed number.

### Calibrating Depth
Match DCF complexity to the request. Do not run a fixed mode. Use the lightest build that answers the question credibly.

**Invariants (always true at any depth):**
- FCF is unlevered (pre-financing) and after-tax, discounted at WACC. Interest never appears in the FCF line.
- Every figure traces to a source, a linked model cell, or a clearly stated assumption.
- When the ask is equity value or per-share, the EV-to-equity bridge runs in full. When EV-only, stop at EV.
- Terminal growth must be less than WACC (g < WACC).
- The terminal year is normalised (steady-state margin, capex approx D&A, change in NWC growing with business).

**Depth spectrum:**
- Full build: client-ready; bottom-up operating forecast; unlevered FCF; bottom-up WACC; both TV methods reconciled; bridge to per share; full sensitivity.
- EBITDA + conversion %: quick/indicative; project EBITDA, apply FCF-conversion % benchmarked to company history; only valid when capex and working capital are stable.
- Consensus-only: take consensus FCF as explicit forecast; where consensus runs out, fade to normalised terminal.
- User-handed inputs: run exactly what is given; bridge to equity/per share if entity data available; label indicative; sanity-check and flag.

### FactSet Data Sourcing
When building a DCF for a real company, **use \`generate_formula\` to pull live data from FactSet** rather than typing static numbers. This is critical for a credible, refreshable model.

**Always source from FactSet (via \`generate_formula\`):**
- Revenue, EBITDA, EBIT, D&A, capex, change in NWC (historicals AND consensus estimates where available)
- Current share price, shares outstanding (diluted), market cap
- Net debt components (total debt, cash, preferred, minority interests)
- Beta (levered or unlevered), risk-free rate proxy (long-dated govt bond yield)
- Peer-group EV/EBITDA exit multiples (for terminal value cross-check)

**Acceptable as typed assumptions (no FactSet source needed):**
- Forecast growth rates, margin assumptions, tax rate
- Terminal growth rate (g), target capital structure weights
- Mid-year convention toggle, valuation date, forecast horizon length
- Any user-provided override or scenario input
- Clearly labeled modeling assumptions when no real source exists. Never present
  these as FactSet-sourced or company-reported data.

**Guidance:**
- Call \`generate_formula\` early (step 1) to establish the data foundation before building calculations on top.
- Use the formula results as cell references in downstream calculations — never re-type the values.
- If FactSet returns #N/A or the formula errors after refresh/recalculation, first make a bounded remediation attempt by checking the formula parameters and regenerating or correcting the formula. Only if that still fails, flag it as a visible data gap and use a clearly-labelled placeholder assumption.
- For consensus estimates (forward revenue, EBITDA), request the estimate items — FactSet covers consensus for most listed equities.

### Build Procedure
1. **Establish forecast & pull FactSet data**: Set horizon (usually 5-10 years; compose Operating Model skills or link existing). Use \`generate_formula\` to source historicals, consensus estimates, market data (price, shares, beta), and balance-sheet items (net debt). Place these in an assumptions or data sheet as the single source of truth.
2. Build unlevered FCF: EBIT x (1-tax) + D&A - capex - change in NWC, per forecast year. Interest never appears. Link to FactSet-sourced historicals/estimates where the forecast overlaps.
3. Build WACC: cost of equity via CAPM (risk-free + beta x ERP), after-tax cost of debt, weighted at target capital structure. Band ~4-16% as sanity check. Risk-free and beta should reference FactSet cells.
4. Discount: multiply each period's FCF by 1/(1+WACC)^t. Apply mid-year convention as toggle; handle stub period if valuation date is mid-year.
5. Terminal value: Gordon growth FCF_n x (1+g)/(WACC-g) AND exit multiple. Back out implied g from multiple and implied multiple from g. Diagnose divergence. Exit multiple can reference FactSet peer data.
6. Bridge to equity: EV = PV(explicit FCF) + PV(TV). Subtract net debt, preferred, minority; add associates. Divide by diluted shares (treasury-stock method). Net debt and shares reference FactSet cells.
7. Sensitise: 2-way data table on WACC x {g or exit multiple}.
8. Validate: run the validation checks below.

### Conventions
- Unlevered FCF: EBIT x (1-tax) + D&A - capex - change in NWC. Start from EBIT, not net income. Tax is marginal/cash rate on EBIT.
- WACC: E/V x Ke + D/V x Kd x (1-tax). Risk-free = long-dated government bond in cash-flow currency. Beta from unlevered peer betas re-levered at target structure.
- Terminal value: always both methods. Gordon g < WACC and <= long-run nominal GDP (~2-3% developed). Exit multiple: terminal EBITDA x exit EV/EBITDA. TV normally 60-80% of EV.
- Mid-year & stub: mid-year = discount at t=0.5, 1.5...; make it a toggle; apply consistently to FCF and TV.
- EV-to-equity bridge: Equity = EV - total debt (incl. finance leases) + cash - preferred - minority + associates/JVs - unfunded pension. Divide by diluted shares (TSM). State net-debt date.
- Currency & units: nominal cash flows at nominal WACC; forecast and discount in same currency; units labelled in headers.

### Updating & Editing a DCF
- Roll valuation date forward when refreshing. Discount periods and stub re-base to new date.
- Re-pull market inputs (share price, risk-free rate, beta, peer exit multiple), not just financials.
- Restate net debt to latest balance-sheet date.
- When extending horizon: old terminal year becomes explicit forecast year (adjust from steady-state to still-growing); add new normalised terminal year.
- Discount factors and TV must be live formulas off assumption cells (hardcoded = silent fail on refresh).

### Validation Rules

**Hard fails (block output):**
- No formula errors (#REF!, #DIV/0!, #VALUE!, #N/A, #NAME?).
- g < WACC.
- EV ties: PV(explicit FCF) + PV(TV) = stated EV (tolerance: max of $1 and 0.5%).
- EV-to-equity bridge ties (tolerance: max of $1 and 0.5%).
- Per share ties: equity value / diluted shares = stated value per share.

**Warnings (flag, do not block):**
- WACC outside ~4-16%.
- TV greater than ~75% of EV.
- Discount factors != 1/(1+WACC)^t for each period, or mid-year/stub not applied consistently.
- FCF line appears to net interest (levered FCF discounted at WACC).
- TV or discount factors are hardcoded rather than live formulas off WACC/g.
- Terminal-year capex materially varies from D&A (terminal year may not be normalised).
- Implied g (from exit multiple) diverges from Gordon g, or g exceeds long-run nominal GDP.
- Company financials (revenue, EBITDA, net debt, shares, beta) are hardcoded instead of sourced from FactSet via \`generate_formula\` — the model will not refresh.`;
